import React from "react";

import { Modal, Typography } from "@packages/ds";
import momentjs from "moment";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { ButtonWrapper, Button } from "ds/Button";

import { Message, Note, Strong } from "./BackupModal.style";

import type { Activity, EnvironmentBackup } from "@packages/client";

type BackupRestoreModalProps = {
  backup: EnvironmentBackup | undefined;
  activity: Activity | undefined;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  handleRestore: () => void;
  projectId?: string;
  environmentId?: string;
  organizationId?: string;
  shouldRedirect: boolean;
};

const BackupRestoreModal = ({
  backup,
  activity,
  show,
  setShow,
  handleRestore,
  projectId,
  environmentId,
  organizationId,
  shouldRedirect
}: BackupRestoreModalProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const userLocale = window.navigator.language;
  momentjs.updateLocale(userLocale, {
    relativeTime: {
      past: "%s ago"
    }
  });

  const handleRestoreBackup = (shouldRedirect: boolean) => {
    handleRestore();
    setShow(false);

    const hasRedirectInfo = organizationId && projectId && environmentId;
    if (shouldRedirect && hasRedirectInfo) {
      navigate(
        `/${organizationId}/${projectId}/${encodeURIComponent(
          environmentId
        )}/backups/history`
      );
    }
  };

  let directive: EnvironmentBackup | Activity | undefined = undefined;
  let commitId: string = "";
  let environment: string = "";

  if (backup) {
    directive = backup;
    commitId = backup.commit_id;
    environment = backup.environment;
  } else if (activity) {
    directive = activity;
    commitId = activity.payload.environment.head_commit;
    environment = activity.environments[0];
  }

  return (
    <Modal
      isOpen={show}
      size="medium"
      onOpenChange={() => setShow(!show)}
      handleClose={() => setShow(false)}
    >
      <Typography.Title tag="h3">
        {intl.formatMessage({
          id: "backups.restore.modal.title"
        })}
      </Typography.Title>
      <Message>
        {intl.formatMessage(
          {
            id: "backups.restore.modal1",
            defaultMessage: `Are you sure you want to restore the backup ${
              directive?.id
            } created ${momentjs(
              directive?.created_at
            ).fromNow()} with commit ID ${commitId}?`
          },
          {
            id: directive?.id,
            created_at: momentjs(directive?.created_at).fromNow(),
            commit_id: commitId,
            br: <br />,
            strong: text => <Strong>{text}</Strong>
          }
        )}
      </Message>
      <Message>
        {intl.formatMessage(
          {
            id: "backups.restore.modal2",
            defaultMessage: `This will delete and then overwrite all data from all running services and files of the ${environment} environment.`
          },
          {
            environment: environment
          }
        )}
      </Message>
      <Note>
        {intl.formatMessage({
          id: "backups.restore.modal3",
          defaultMessage: "Note: this action can not be undone."
        })}
      </Note>
      <ButtonWrapper spacing="modal" justifyContent="end">
        <Button
          variant="secondary"
          onClick={() => setShow(false)}
          analyticId="cancel"
        >
          {intl.formatMessage({
            id: "cancel"
          })}
        </Button>
        <Button
          onClick={() => handleRestoreBackup(shouldRedirect)}
          analyticId="backups.restore.modal.action"
        >
          {intl.formatMessage({
            id: "backups.restore.modal.action"
          })}
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};

export default BackupRestoreModal;
